import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './shadcn/button';
import { Card, CardContent } from './shadcn/card';
import { motion } from 'framer-motion';
import { isLandingOnly } from '../utils/buildMode';
import {
  Upload,
  Brain,
  BarChart3,
  Shield,
  Clock,
  Users,
  Zap,
  CheckCircle,
  FileText,
  Tag,
  CreditCard,
  Mail
} from 'lucide-react';

function AboutUs() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About NeuralVision
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Upload traffic videos and get accurate vehicle movement counts with automatic timestamp extraction.
            Generate TSV and Excel reports for your transportation planning needs.
          </p>
        </motion.div>

        {/* How It Works Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-lg mr-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">How NeuralVision Works</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Upload className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-center mb-3">1. Upload Videos</h3>
                <p className="text-gray-600 text-center">Upload traffic videos to our secure platform. We'll automatically detect the video length.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-center mb-3">2. AI Processing</h3>
                <p className="text-gray-600 text-center">Our AI analyzes your video and generates traffic movement counts.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-center mb-3">3. Get Results</h3>
                <p className="text-gray-600 text-center">Receive comprehensive TMC reports in both TSV and Excel formats.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Processing Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-lg mr-4">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Video Processing Features</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Advanced AI Analysis</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Automated timestamp extraction from videos</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Detect and count movements with high precision</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Multiple object detection for different vehicle types</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Processing Capabilities</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Rapid processing - get TMC reports within minutes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Support for multiple video formats (MP4, AVI, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">File size support up to 500MB (per file)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-3 rounded-lg mr-4">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Output</h2>
            </div>

            <p className="text-lg text-gray-600 mb-6 text-center">
              Get comprehensive Traffic Movement Count reports in multiple formats for easy integration with your systems
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <FileText className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">TSV Reports</h3>
                  <p className="text-gray-600">Tab-separated values for easy import into spreadsheets and analysis tools</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <FileText className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Excel Reports</h3>
                  <p className="text-gray-600">Formatted Excel files with built-in feature to help with your needs</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <BarChart3 className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Detailed Analytics</h3>
                  <p className="text-gray-600">Comprehensive data on vehicle counts and movement patterns</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>

        {/* Upcoming Analytics Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 rounded-lg mr-4">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Upcoming Analytics Features</h2>
            </div>

            <p className="text-lg text-gray-600 mb-6 text-center">
              We're continuously enhancing our platform with advanced analytics capabilities
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Advanced Dashboards</h3>
                <p className="text-gray-600">Customizable dashboards for real-time visualization of traffic patterns and trends.</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Anomaly Detection</h3>
                <p className="text-gray-600">Intelligent systems to identify unusual traffic patterns and potential issues.</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Predictive Modeling</h3>
                <p className="text-gray-600">Forecast traffic volumes based on historical data and patterns.</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Integration APIs</h3>
                <p className="text-gray-600">API access to integrate our analytics into your existing systems.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Security and Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-gray-600 to-gray-800 p-3 rounded-lg mr-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Security and Reliability</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-600 mb-4">Data Security</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Enterprise-grade encryption for all data</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Secure data handling compliant with municipal standards</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Regular security audits and updates</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-600 mb-4">Platform Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">98.7% accuracy rate in vehicle detection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">24/7 dedicated technical support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Dedicated onboarding support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-2xl mx-auto text-center mt-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Transform Your Traffic Analysis?</h2>
          <p className="text-lg text-gray-600 mb-8">
            {isLandingOnly
              ? 'Contact our team to discuss pricing, volume discounts, and how NeuralVision can support your projects.'
              : 'Join transportation professionals using NeuralVision for accurate Traffic Movement Count data.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={isLandingOnly ? '/contact-us' : '/register'}>
              <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-3 text-lg">
                {isLandingOnly ? 'Contact Us' : 'Get Started'}
                {isLandingOnly ? <Mail className="ml-2 h-5 w-5" /> : <CreditCard className="ml-2 h-5 w-5" />}
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-2 px-8 py-3 text-lg">
                View Pricing
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default AboutUs;